import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, Zap, Crown, Loader2, CheckCircle2, ArrowRight, Sparkles } from "lucide-react";
import { useData } from "@/contexts/DataContext";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { functions } from "@/lib/firebase";
import { httpsCallable } from "firebase/functions";
import { Checkout } from 'capacitor-razorpay';
import { Capacitor } from '@capacitor/core';
import confetti from "canvas-confetti";
import { Button } from "@/components/ui/button";
import { PRO_PLANS, RENEWAL_DISCLAIMER } from "@/lib/constants";

export function ProUpgradeModal() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const {
    isProUpgradeModalOpen,
    closeProUpgradeModal,
    rewardPoints: totalPoints,
    proModeExpiry,
    businessProfile,
    upgradeToProWithPoints,
    subscriptionId,
    subscriptionStatus
  } = useData();

  const [selectedPlan, setSelectedPlan] = useState<typeof PRO_PLANS[0] | null>(null);

  // 🛡️ Identify if user is on active auto-pay to prevent points loophole
  const isControllableSub = !!subscriptionId && (subscriptionStatus === 'active' || subscriptionStatus === 'paused');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const { trialsUsed } = useData();
  const isEligibleForTrial = useMemo(() => {
    return !proModeExpiry && !trialsUsed?.any;
  }, [proModeExpiry, trialsUsed]);

  useEffect(() => {
    if (isProUpgradeModalOpen && !selectedPlan) {
      setSelectedPlan(PRO_PLANS.find(p => p.days === 30) || PRO_PLANS[2]);
    }
  }, [isProUpgradeModalOpen, selectedPlan]);

  const handleCashPayment = async (days: number) => {
    if (isProcessingPayment) return;
    setIsProcessingPayment(true);
    try {
      const baselineExpiry = proModeExpiry || "";
      localStorage.setItem("dh_baseline_expiry", baselineExpiry);

      const createSub = httpsCallable(functions, 'createRazorpaySubscription');

      const response = await createSub({
        days,
        planName: selectedPlan?.label || `${days} Days Pro`,
        targetTier: "pro",
      });

      const data = response.data as { success: boolean, subscriptionId: string };

      if (!data.success || !data.subscriptionId) {
        throw new Error("Could not initialize payment service.");
      }

      // 🛡️ HARDCODED LIVE KEY
      const rzpKey = "rzp_live_StU8i2gpLCYQOq";

      const prefill: any = {};
      const contactNum = businessProfile?.businessPhone?.replace(/\D/g, '').slice(-10) || user?.phoneNumber?.replace(/\D/g, '').slice(-10);
      if (contactNum && contactNum.length >= 10) prefill.contact = contactNum;

      const emailStr = businessProfile?.businessEmail || user?.email;
      if (emailStr && emailStr.trim() !== "") prefill.email = emailStr.trim();

      const nameStr = businessProfile?.ownerName || businessProfile?.businessName || user?.displayName;
      if (nameStr && nameStr.trim() !== "") prefill.name = nameStr.trim();

      if (Capacitor.isNativePlatform()) {
        const nativeOptions: any = {
          key: rzpKey,
          subscription_id: data.subscriptionId,
          name: "ASL DIGITAL SOLUTIONS",
          description: `${selectedPlan?.label || days + ' Days'} Premium Access`,
          currency: "INR",
          amount: ((selectedPlan?.price || 0) * 100).toString(),
          theme: { color: "#8B1A1A" }
        };

        if (Object.keys(prefill).length > 0) {
          nativeOptions.prefill = prefill;
        }

        await Checkout.open(nativeOptions);
        closeProUpgradeModal();
        setLocation("/payment-status");

      } else {
        const webOptions: any = {
          key: rzpKey,
          subscription_id: data.subscriptionId,
          name: "DAILYHISAAB PRO",
          description: `${selectedPlan?.label || days + ' Days'} Premium Access`,
          theme: { color: "#8B1A1A" },
          handler: function (response: any) {
            closeProUpgradeModal();
            setLocation("/payment-status");
          }
        };

        if (Object.keys(prefill).length > 0) {
          webOptions.prefill = prefill;
        }

        const rzp = new (window as any).Razorpay(webOptions);
        rzp.on('payment.failed', function (response: any) {
          toast({
            title: "Payment Failed",
            description: response.error.description,
            variant: "destructive"
          });
          setIsProcessingPayment(false);
        });
        rzp.open();
      }

    } catch (error: any) {
      if (error.code !== 0 && error.message !== "Payment cancelled by user") {
        toast({
          title: "Payment Interrupted",
          description: error.message || "Could not open the payment window.",
          variant: "destructive"
        });
      }
      setIsProcessingPayment(false);
    }
  };

  const handleConfirmUpgrade = async () => {
    if (!selectedPlan) return;
    const success = await upgradeToProWithPoints(selectedPlan);
    if (success) {
      confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 }, colors: ["#fbbf24", "#f59e0b", "#d97706", "#ffffff"] });
      toast({ title: `Premium activated for ${selectedPlan.label.replace(" Premium", "")}!` });
    }
    setShowConfirmDialog(false);
    closeProUpgradeModal();
  };

  return (
    <>
      <Dialog open={isProUpgradeModalOpen} onOpenChange={(open) => { if (!isProcessingPayment && !open) closeProUpgradeModal(); }}>
        <DialogContent className="max-w-full w-full h-[100dvh] p-0 m-0 rounded-none border-none bg-[#FFFBF2] dark:bg-[#1A1614] flex flex-col shadow-none overflow-hidden z-[300]">
          <div className="sr-only"><DialogTitle>Go Premium</DialogTitle></div>
          <div className="px-4 pt-[calc(env(safe-area-inset-top)+1rem)] pb-4 bg-white dark:bg-slate-900 shadow-sm border-b border-slate-200 dark:border-slate-800 flex items-center justify-between shrink-0">
            <button onClick={closeProUpgradeModal} disabled={isProcessingPayment} className="w-8 h-8 flex items-center justify-center bg-slate-100 dark:bg-slate-800 rounded-full active:scale-95 transition-transform disabled:opacity-50">
              <ChevronLeft className="w-5 h-5 text-slate-700 dark:text-slate-300" />
            </button>
            <h2 className="font-black text-lg text-slate-900 dark:text-white">Select Plan</h2>
            <div className="flex items-center gap-1 bg-amber-50 dark:bg-amber-900/30 px-3 py-1.5 rounded-full border border-amber-200 dark:border-amber-800/50 shadow-sm">
              <Zap className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
              <span className="text-xs font-black text-amber-600">{totalPoints.toLocaleString()} pts</span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-8 space-y-5 pb-32">
            {PRO_PLANS.map((plan) => {
              const isSelected = selectedPlan?.days === plan.days;
              return (
                <div key={plan.days} onClick={() => !isProcessingPayment && setSelectedPlan(plan)} className={`relative p-5 rounded-[24px] border-2 transition-all cursor-pointer mt-2 ${isSelected ? "border-[#8B1A1A] bg-[#8B1A1A]/5 dark:bg-[#8B1A1A]/10 shadow-md" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"} ${isProcessingPayment ? "opacity-70 pointer-events-none" : ""}`}>
                  {plan.days === 30 && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-[10px] font-black uppercase tracking-widest px-4 py-1 rounded-full shadow-sm z-10">Best Value</div>}
                  <div className="absolute top-5 right-5"><div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-[#8B1A1A]' : 'border-slate-300 dark:border-slate-600'}`}>{isSelected && <div className="w-3 h-3 bg-[#8B1A1A] rounded-full" />}</div></div>
                  <div className="flex items-center gap-2 mb-2 mt-1"><h3 className={`text-lg font-black tracking-wide ${isSelected ? 'text-[#8B1A1A] dark:text-red-400' : 'text-slate-900 dark:text-white'}`}>{plan.label}</h3><Crown className="w-4 h-4 text-amber-500" /></div>
                  <div className="flex items-baseline gap-2 mb-4"><span className="text-2xl font-black text-slate-900 dark:text-white">₹{plan.price}</span><span className="text-sm font-bold text-slate-400 line-through">₹{plan.originalPrice}</span></div>
                  <div className="grid grid-cols-2 gap-2 border-t border-slate-100 dark:border-slate-800 pt-4">
                    <div className="text-center flex flex-col items-center"><span className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Validity</span><span className="text-xs font-bold text-slate-700 dark:text-slate-300">{plan.days} DAYS</span></div>
                    <div className="text-center flex flex-col items-center border-l border-slate-100 dark:border-slate-800"><span className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Total Bills</span><span className="text-xs font-bold text-slate-700 dark:text-slate-300">UNLIMITED</span></div>
                  </div>
                </div>
              );
            })}
          </div>

          {selectedPlan && (
            <div className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 px-4 pt-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] flex flex-col gap-2 shrink-0 z-50 shadow-[0_-4px_15px_rgba(0,0,0,0.05)]">
              <div className="flex items-center gap-3 w-full">
                <div className="flex flex-col min-w-[80px] pl-1">
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{selectedPlan.days} DAYS</span>
                  <span className="text-xl font-black text-slate-900 dark:text-white leading-none mt-1">₹{selectedPlan.price}</span>
                </div>
                <div className="flex gap-2 flex-1">
                  {totalPoints >= selectedPlan.points && (
                    isControllableSub ? (
                      <div className="flex-1 py-3.5 rounded-xl font-bold text-xs bg-slate-100 dark:bg-slate-800 text-slate-400 border border-slate-200 dark:border-slate-700 flex flex-col items-center justify-center leading-tight">
                        <span>Locked</span>
                        <span className="text-[8px] font-medium opacity-80">(Auto-Pay Active)</span>
                      </div>
                    ) : (
                      <button onClick={() => setShowConfirmDialog(true)} disabled={isProcessingPayment} className="flex-1 py-3.5 rounded-xl font-bold text-xs bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 flex items-center justify-center gap-1 active:scale-95 transition-all shadow-sm disabled:opacity-50">
                        <Zap className="w-3 h-3 fill-amber-500 text-amber-500" /> Use {selectedPlan.points}
                      </button>
                    )
                  )}
                  <button disabled={isProcessingPayment} onClick={() => handleCashPayment(selectedPlan.days)} className="flex-[1.2] py-3.5 rounded-xl bg-[#8B1A1A] hover:bg-[#631212] text-white font-black text-[15px] active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center shadow-md">
                    {isProcessingPayment ? <Loader2 className="w-5 h-5 animate-spin" /> : isEligibleForTrial ? "Start 7-Day Free Trial" : "Subscribe"}
                  </button>
                </div>
              </div>
              <p className="text-[9px] font-bold text-slate-400 dark:text-slate-500 text-center uppercase tracking-widest pt-1">{RENEWAL_DISCLAIMER}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-full w-full h-[100dvh] p-0 m-0 rounded-none border-none bg-[#FFFBF2] dark:bg-[#1A1614] flex flex-col shadow-none overflow-hidden z-[400]">
          <div className="sr-only"><DialogTitle>Confirm Points Upgrade</DialogTitle></div>
          <div className="px-4 pt-[calc(env(safe-area-inset-top)+1rem)] pb-4 flex items-center shrink-0">
            <button onClick={() => setShowConfirmDialog(false)} className="w-10 h-10 flex items-center justify-center bg-white dark:bg-slate-800 rounded-full shadow-sm border border-slate-100 dark:border-slate-700 active:scale-95">
              <ChevronLeft className="w-6 h-6 text-slate-800 dark:text-white" />
            </button>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
            <div className="relative mb-8">
              <div className="w-24 h-24 bg-amber-100 dark:bg-amber-900/30 rounded-[32px] flex items-center justify-center rotate-12 shadow-inner"><Crown className="w-12 h-12 text-amber-600 dark:text-amber-500 -rotate-12" /></div>
              <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-white dark:bg-slate-800 rounded-full shadow-lg flex items-center justify-center"><Zap className="w-5 h-5 text-amber-500 fill-amber-500" /></div>
            </div>
            <h3 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-4">Confirm Upgrade</h3>
            <div className="space-y-2 mb-8">
              <p className="text-slate-500 dark:text-slate-400 font-medium">You are redeeming <span className="font-black text-amber-600 dark:text-amber-400">{selectedPlan?.points.toLocaleString()} points</span></p>
              <div className="flex items-center justify-center gap-2 py-2">
                <span className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">Your Balance</span><ArrowRight className="w-3 h-3 text-slate-300" />
                <span className="bg-amber-50 dark:bg-amber-900/20 px-3 py-1 rounded-full text-amber-700 dark:text-amber-400 font-black text-xs">{selectedPlan ? (totalPoints - selectedPlan.points).toLocaleString() : 0} pts</span>
              </div>
            </div>
            <div className="w-full bg-white dark:bg-slate-900 p-6 rounded-[32px] border border-slate-100 dark:border-slate-800 shadow-sm mb-12">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 block">New Plan</span>
              <p className="text-xl font-black text-[#8B1A1A] dark:text-red-400">{selectedPlan?.label}</p>
              <div className="flex items-center justify-center gap-2 mt-3 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="w-4 h-4" /><span className="text-xs font-bold uppercase tracking-wider">{selectedPlan?.days} Days Validity</span></div>
            </div>
            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-6 flex items-center gap-2"><Sparkles className="w-3 h-3" /> One-time extension • No automatic renewal</p>
          </div>
          <div className="p-4 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] flex flex-col gap-3 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800">
            <Button onClick={handleConfirmUpgrade} className="w-full h-16 rounded-[20px] bg-[#8B1A1A] hover:bg-[#631212] text-white font-black text-lg uppercase tracking-widest shadow-xl shadow-red-900/20 active:scale-95 transition-all">Yes, Upgrade Now</Button>
            <button onClick={() => setShowConfirmDialog(false)} className="w-full h-14 rounded-[20px] font-bold text-slate-500 dark:text-slate-400 bg-transparent active:scale-95 transition-all">Cancel</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}